import { useCallback, useEffect, useState } from 'react';
import {
  getInventory,
  getUnopenedPoolOffers,
  type ApiClient,
  type IframePoolOrderItem,
  type IframeUnopenedOffer,
} from '../../modules/api';

export interface MyPacksData {
  unopened: IframeUnopenedOffer[];
  unopenedLoading: boolean;

  inventory: IframePoolOrderItem[];
  inventoryTotal: number;
  inventoryLoading: boolean;

  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Parallel fetch for the My Packs page — unopened packs + opened inventory.
 * Deliveries are intentionally not fetched: shipping is not a thing inside
 * the iframe casino context, that tab renders its own empty state.
 */
export function useMyPacksData(client: ApiClient): MyPacksData {
  const [unopened, setUnopened] = useState<IframeUnopenedOffer[]>([]);
  const [unopenedLoading, setUnopenedLoading] = useState(true);

  const [inventory, setInventory] = useState<IframePoolOrderItem[]>([]);
  const [inventoryTotal, setInventoryTotal] = useState(0);
  const [inventoryLoading, setInventoryLoading] = useState(true);

  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    setUnopenedLoading(true);
    setInventoryLoading(true);

    const [unopenedRes, invRes] = await Promise.allSettled([
      getUnopenedPoolOffers(client),
      getInventory(client, { per: 32 }),
    ]);

    if (unopenedRes.status === 'fulfilled') {
      setUnopened(unopenedRes.value);
    } else {
      setError(unopenedRes.reason instanceof Error ? unopenedRes.reason.message : 'Failed to load');
    }
    setUnopenedLoading(false);

    if (invRes.status === 'fulfilled') {
      setInventory(invRes.value.items);
      setInventoryTotal(invRes.value.totalCount);
    } else if (!error) {
      setError(invRes.reason instanceof Error ? invRes.reason.message : 'Failed to load');
    }
    setInventoryLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client]);

  useEffect(() => {
    void load();
  }, [load]);

  return {
    unopened,
    unopenedLoading,
    inventory,
    inventoryTotal,
    inventoryLoading,
    error,
    refetch: load,
  };
}
